import React, { useState } from "react";
import { Link } from "react-router-dom";
import { X } from "lucide-react";
import { ReadableCard, ReadableNavButton } from "../Sticker";
import {
  HOST_ONBOARDING_RESUME_PATH,
  shouldShowHostOnboardingResume,
} from "../../lib/hostOnboarding";

const DISMISS_KEY = "rsvp.hostOnboardingResumeDismissed";

function isDismissedForAccount(accountId) {
  if (!accountId) return false;
  try {
    return sessionStorage.getItem(`${DISMISS_KEY}:${accountId}`) === "1";
  } catch {
    return false;
  }
}

function dismissForAccount(accountId) {
  if (!accountId) return;
  try {
    sessionStorage.setItem(`${DISMISS_KEY}:${accountId}`, "1");
  } catch {
    /* ignore */
  }
}

/**
 * Soft nudge for hosts who deferred setup before publishing something / finishing.
 * Session-dismissible; reappears next browser session until onboarding completes.
 */
export default function HostOnboardingResumeBanner({ account }) {
  const accountId = account?.id || account?.organizer_id || account?.email || "me";
  const [dismissed, setDismissed] = useState(() => isDismissedForAccount(accountId));

  if (!shouldShowHostOnboardingResume(account) || dismissed) return null;

  function handleDismiss() {
    dismissForAccount(accountId);
    setDismissed(true);
  }

  return (
    <section className="max-w-page mx-auto px-4 mt-3" data-testid="host-onboarding-resume-banner">
      <ReadableCard className="relative p-4 pr-12">
        <button
          type="button"
          onClick={handleDismiss}
          className="absolute top-2 right-2 rounded-md2 p-1.5 min-w-[32px] min-h-[32px] flex items-center justify-center text-ink/45 hover:text-ink/70 hover:bg-black/[0.04]"
          aria-label="Dismiss host setup reminder"
          data-testid="host-onboarding-resume-dismiss"
        >
          <X className="w-3.5 h-3.5" strokeWidth={2.5} aria-hidden />
        </button>
        <p className="font-ui font-semibold text-[15px] text-ink">Finish host setup</p>
        <p className="font-ui text-[13px] text-ink/70 mt-1 text-pretty leading-snug">
          Publish an event or school so guests have something to open.
        </p>
        <ReadableNavButton
          as={Link}
          to={HOST_ONBOARDING_RESUME_PATH}
          variant="primary"
          className="mt-3 min-h-[44px] px-4 text-[14px]"
          data-testid="host-onboarding-resume-cta"
        >
          Continue setup →
        </ReadableNavButton>
      </ReadableCard>
    </section>
  );
}
